// produces Tabulator column objects (UI)
import type { ColumnDefinition, ColumnDefinitionAlign } from 'tabulator-tables'
import { useNuxtApp } from '#app'
import { lucideIcon } from '~/utils/icons'
import { utcToZonedTime  } from 'date-fns-tz'

export function editColumn(onEdit: (rowData: any) => void): ColumnDefinition {
  const { $i18n } = useNuxtApp()
  const align: ColumnDefinitionAlign = 'center'

  return {
    title: $i18n.t('common.action.edit'),
    headerLocaleId: 'common.action.edit',
    field:'_edit',
    headerSort: false,
    headerHozAlign: align,
    resizable: false,
    hozAlign: align,
    width: 65,
    minWidth: 65,
    maxWidth: 65,
    frozen: true,
    formatter: () => `
      <button data-action="edit" 
        class="bg-transparent text-yellow-600 hover:text-yellow-700 transition-transform duration-300 cursor-pointer group" 
        title="${$i18n.t('common.action.edit')}">
        <span class=" transition-transform duration-300 group-hover:scale-[1.07]">
          ${lucideIcon('pencil')}
        </span>
      </button>
    `,
    cellClick: (_e: any, cell: any) => {
      const rowData = cell.getData()
      onEdit(rowData)
    }
  }
}

export function viewColumn(onEdit: (rowData: any) => void): ColumnDefinition {
  const { $i18n } = useNuxtApp()
  const align: ColumnDefinitionAlign = 'center'

  return {
    title: $i18n.t('common.action.view'),
    headerLocaleId: 'common.action.view',
    field:'_view',
    headerSort: false,
    headerHozAlign: align,
    resizable: false,
    hozAlign: align,
    width: 65,
    minWidth: 65,
    maxWidth: 65,
    frozen: true,
    formatter: () => `
      <button data-action="edit" 
        class="bg-transparent text-yellow-600 hover:text-yellow-700 transition-transform duration-300 cursor-pointer group" 
        title="${$i18n.t('common.action.view')}">
        <span class=" transition-transform duration-300 group-hover:scale-[1.07]">
          ${lucideIcon('folder-open')}
        </span>
      </button>
    `,
    cellClick: (_e: any, cell: any) => {
      const rowData = cell.getData()
      onEdit(rowData)
    }
  }
}

export function deleteColumn(onDelete: (rowData: any) => void): ColumnDefinition {
  const { $i18n } = useNuxtApp()
  const align: ColumnDefinitionAlign = 'center'

  return {
    title: $i18n.t('common.action.delete'),
    headerLocaleId: 'common.action.delete',
    field:'_delete',
    headerSort: false,
    headerHozAlign: align,
    resizable: false,
    hozAlign: align,
    width: 65,
    minWidth: 65,
    maxWidth: 65,
    frozen: true,
    formatter: () => `
      <button data-action="delete" 
        class="bg-transparent text-red-600 hover:text-red-700 transition-transform duration-300 cursor-pointer group" 
        title="${$i18n.t('common.action.delete')}">
        <span class=" transition-transform duration-300 group-hover:scale-[1.07]">
          ${lucideIcon('trash')}
        </span>
      </button>
    `,
    cellClick: (_e: any, cell: any) => {
      const rowData = cell.getData()
      onDelete(rowData)
    }
  }
}

export function statusColumn(
  field: string = 'isActive',
  options?: {
    width?: number | string
    align?: ColumnDefinitionAlign,
    frozen?: boolean
  }
): ColumnDefinition {
  const { $i18n } = useNuxtApp()
  const width = options?.width ?? 90
  const align = options?.align ?? 'center'
  const frozen = options?.frozen ?? false;

  return {
    title: $i18n.t('common.object.status.label'),
    headerLocaleId: 'common.object.status.label',
    field,
    width,
    hozAlign: align,
    headerHozAlign: align,
    resizable: false,
    frozen: frozen,
    formatter: (cell) => {
      const active = isActive(cell.getValue() as any);
      const texto = active
        ? $i18n.t('common.object.status.active')
        : $i18n.t('common.object.status.inactive')

      const bgColor = active ? '#009689' : '#d50000'
      const textColor = '#ffffff'

      return `
       <span class="industrial-status-badge w-full" style="
        background-color: ${bgColor};
        color: ${textColor};
        font-weight: 600;
        border-radius: 5px;
        padding: 3px 6px 3px 6px;
      ">${texto}</span>
      `
    }
  }
}

// "valor / requerido" de inspecciones segun tabla ANSI: rojo mientras falten, verde al completarse
export function requiredInspectionsColumn(
  labelKey: string,
  getEntered: (row: any) => number,
  getRequired: (row: any) => number | null | undefined,
  options?: { field?: string, width?: number }
): ColumnDefinition {
  const { $i18n } = useNuxtApp()
  const width = options?.width ?? 110

  return {
    title: $i18n.t(labelKey),
    headerLocaleId: labelKey,
    field: options?.field ?? 'requiredInspections',
    hozAlign: 'right',
    headerHozAlign: 'right',
    headerSort: false,
    width,
    minWidth: width,
    maxWidth: width,
    formatter: (cell) => {
      const row = cell.getData()
      const entered = getEntered(row) ?? 0
      const required = getRequired(row)
      // Sin cantidad requerida (lote que no exige las muestras de la tabla ANSI) basta con una.
      const reached = required ? entered >= required : entered > 0
      const color = reached ? '#16a34a' : '#dc2626'
      return `<span style="color: ${color}; font-weight: 600;">${entered} / ${required || '-'}</span>`
    }
  }
}

export function dateColumn(
  field: string,
  labelKey: string = 'common.object.created_at',
  options?: {
    width?: number
    align?: ColumnDefinitionAlign
    locale?: string
    dateOptions?: Intl.DateTimeFormatOptions,
    frozen?: boolean,
    //timezone?: string
  }
): ColumnDefinition {
  const { $i18n } = useNuxtApp()
  const selectedTimezone = useState<string>(
    'selected-timezone',
    () => 'America/Guayaquil'
  )
  const width = options?.width ?? 127
  const align = options?.align ?? 'right';
  const frozen = options?.frozen ?? false;

  const dateOptions: Intl.DateTimeFormatOptions =
    {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      //timeZone: selectedTimezone.value
    }
  
  return {
    title: $i18n.t(labelKey),
    headerLocaleId: labelKey,
    field,
    hozAlign: align,
    headerHozAlign: align,
    resizable: false,
    width,
    minWidth: width,
    maxWidth: width,
    frozen: frozen,
    formatter: (cell) => {
      const locale = options?.locale ?? $i18n.locale.value
      const value = cell.getValue()
      if (!value) return '-'

      const normalized = value.includes('Z') || value.includes('+')
        ? value
        : value + 'Z';

      const date = new Date(normalized);
      const zonedDate = utcToZonedTime(date, selectedTimezone.value)

      if (isNaN(zonedDate.getTime())) return '-'

      const formatted = new Intl.DateTimeFormat(locale, dateOptions).format(zonedDate)

      return `<span style="
        text-align:${align};
        width:100%;
        color:#444;
      ">${formatted}</span>`
    }
  }
}
